'use client'

import type { ReactNode } from 'react'
import { useCallback, useId, useState } from 'react'

import { useIntl } from 'react-intl'
import { css, styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import {
  type ListingFiltersState,
  type ListingSearchContextValue,
  useListingSearch,
} from '@/components/listings/search/ListingSearchProvider'
import {
  DEFAULT_THRESHOLD,
  THRESHOLD_STEPS,
  type ThresholdStep,
  getAppliedThreshold,
  hasCategorySelection,
  isThresholdStep,
} from '@/components/listings/search/ListingSearchProvider/categoryThreshold'
import type { AppliedProps } from '@/components/listings/search/SearchFilters/types'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import AppliedFilterSection from '../../shared/AppliedFilterSection'

const toStep = (value: number | null): ThresholdStep =>
  isThresholdStep(value) ? value : DEFAULT_THRESHOLD

// Position of a step along the track, in percent: the stops are evenly spaced.
const toPosition = (step: ThresholdStep) =>
  (THRESHOLD_STEPS.indexOf(step) / (THRESHOLD_STEPS.length - 1)) * 100

type SliderSize = 'default' | 'large'

// Thumb diameter in rem. The large size is for touch: a 1.5rem thumb in a
// 2.75rem tall touch area.
const thumbSize: Record<SliderSize, number> = { default: 1, large: 1.5 }
const railHeight: Record<SliderSize, number> = { default: 1, large: 2.75 }

const SliderRoot = styled.div<{ $size: SliderSize; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0 0.5rem;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`

const Rail = styled.div<{ $size: SliderSize }>`
  position: relative;
  height: ${({ $size }) => railHeight[$size]}rem;
`

const trackStyles = css`
  position: absolute;
  top: 50%;
  left: 0;
  height: 0.25rem;
  border-radius: 0.125rem;
  transform: translateY(-50%);
  pointer-events: none;
`

const Track = styled.div`
  ${trackStyles};
  right: 0;
  background: ${designColors.neutral.mediumGray};
`

const Fill = styled.div<{ $disabled: boolean }>`
  ${trackStyles};
  background: ${({ $disabled }) =>
    $disabled
      ? designColors.neutral.disabled
      : designColors.primary.brightBlue};
`

const Dot = styled.span<{ $filled: boolean }>`
  position: absolute;
  top: 50%;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  background: ${({ $filled }) =>
    $filled ? designColors.primary.brightBlue : designColors.neutral.disabled};
`

const Thumb = styled.span<{ $size: SliderSize; $disabled: boolean }>`
  position: absolute;
  top: 50%;
  width: ${({ $size }) => thumbSize[$size]}rem;
  height: ${({ $size }) => thumbSize[$size]}rem;
  box-sizing: border-box;
  border-radius: 50%;
  border: 0.125rem solid
    ${({ $disabled }) =>
      $disabled
        ? designColors.neutral.disabled
        : designColors.primary.brightBlue};
  background: ${designColors.neutral.white};
  transform: translate(-50%, -50%);
  pointer-events: none;
`

// The native range input does the interaction and accessibility; it sits
// invisibly over the drawn track. It extends half a thumb past each end so
// its thumb centre lines up with the first and last stops.
const Range = styled.input<{ $size: SliderSize }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $size }) => -thumbSize[$size] / 2}rem;
  width: ${({ $size }) => `calc(100% + ${thumbSize[$size]}rem)`};
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  appearance: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: ${({ $size }) => thumbSize[$size]}rem;
    height: ${({ $size }) => thumbSize[$size]}rem;
  }

  &::-moz-range-thumb {
    width: ${({ $size }) => thumbSize[$size]}rem;
    height: ${({ $size }) => thumbSize[$size]}rem;
    border: 0;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:focus-visible ~ ${Thumb} {
    box-shadow:
      0 0 0 0.125rem ${designColors.neutral.white},
      0 0 0 0.25rem ${designColors.primary.brightBlue};
  }
`

const Ticks = styled.div`
  position: relative;
  height: 1.125rem;
`

const Tick = styled.span<{ $active: boolean; $disabled: boolean }>`
  ${Typography.Body4};
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  color: ${({ $active, $disabled }) =>
    $disabled
      ? designColors.neutral.disabled
      : $active
        ? designColors.primary.brightBlue
        : designColors.neutral.darkGray};
`

const toThresholdSurface = (
  rankingSurface: ListingSearchContextValue['rankingSurface']
): 'alp' | 'storefront' =>
  rankingSurface === 'storefront' ? 'storefront' : 'alp'

/**
 * Fires `category_threshold_adjusted` for a threshold about to be applied
 * (no-op when it matches the current one). Takes the search context so the
 * filters modal, which receives it as a prop, can use it too.
 */
export const useTrackThresholdAdjusted = (
  searchCtx: Pick<ListingSearchContextValue, 'rankingSurface' | 'state'>
) => {
  const { track } = useAnalytics()
  const surface = toThresholdSurface(searchCtx.rankingSurface)
  const currentThreshold = searchCtx.state.filters.categoryThreshold

  return useCallback(
    (step: number | null | undefined) => {
      if (step == null || currentThreshold === step) return

      track('category_threshold_adjusted', {
        previous_threshold: currentThreshold,
        new_threshold: step,
        surface,
      })
    },
    [currentThreshold, surface, track]
  )
}

/**
 * Applies a new threshold to the search state and fires
 * `category_threshold_adjusted` (no-op when the value is unchanged).
 */
const useApplyThreshold = () => {
  const searchCtx = useListingSearch()
  const { state, updateFilters } = searchCtx
  const trackThresholdAdjusted = useTrackThresholdAdjusted(searchCtx)
  const currentThreshold = state.filters.categoryThreshold

  return useCallback(
    (step: ThresholdStep) => {
      if (currentThreshold === step) return

      trackThresholdAdjusted(step)
      updateFilters({ categoryThreshold: step })
    },
    [currentThreshold, trackThresholdAdjusted, updateFilters]
  )
}

type ThresholdSliderProps = {
  value: ThresholdStep
  onChange: (step: ThresholdStep) => void
  // Fires when the buyer lets go of the slider (pointer up, key up or blur),
  // so callers can apply the value once instead of at every stop mid-drag.
  onRelease?: () => void
  disabled?: boolean
  size?: SliderSize
  // Id of the element that labels the slider.
  labelledBy?: string
  className?: string
}

/**
 * Presentational slider over the fixed threshold steps (5/10/25/50/100%):
 * a track with a dot at each stop, filled up to the thumb, and the stop
 * labels centred under their stops. The range input works on step indexes so
 * every stop is evenly spaced.
 */
export function ThresholdSlider({
  value,
  onChange,
  onRelease,
  disabled = false,
  size = 'default',
  labelledBy,
  className,
}: ThresholdSliderProps) {
  const intl = useIntl()
  const index = THRESHOLD_STEPS.indexOf(value)
  const position = toPosition(value)

  return (
    <SliderRoot className={className} $size={size} $disabled={disabled}>
      <Rail $size={size}>
        <Track />
        <Fill $disabled={disabled} style={{ width: `${position}%` }} />
        {!disabled &&
          THRESHOLD_STEPS.map((step, i) => (
            <Dot
              key={step}
              $filled={i <= index}
              style={{ left: `${toPosition(step)}%` }}
            />
          ))}
        <Range
          type="range"
          $size={size}
          min={0}
          max={THRESHOLD_STEPS.length - 1}
          step={1}
          value={index}
          disabled={disabled}
          aria-labelledby={labelledBy}
          aria-valuetext={intl.formatMessage(
            { id: 'Listing.filters.categoryThreshold.valueText' },
            { threshold: value }
          )}
          onChange={(e) => {
            const step = THRESHOLD_STEPS[Number(e.target.value)]
            if (step !== undefined) onChange(step)
          }}
          onPointerUp={onRelease}
          onKeyUp={onRelease}
          onBlur={onRelease}
        />
        <Thumb
          $size={size}
          $disabled={disabled}
          style={{ left: `${position}%` }}
        />
      </Rail>
      <Ticks aria-hidden="true">
        {THRESHOLD_STEPS.map((step) => (
          <Tick
            key={step}
            $active={step === value}
            $disabled={disabled}
            style={{ left: `${toPosition(step)}%` }}
          >
            <FormattedMessage
              id={`Listing.filters.categoryThreshold.${step}`}
            />
          </Tick>
        ))}
      </Ticks>
    </SliderRoot>
  )
}

type PanelVariant = 'rail' | 'modal' | 'mobile'

const Panel = styled.div<{ $variant: PanelVariant }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $variant }) => ($variant === 'modal' ? 0.75 : 0.625)}rem;
  padding: ${({ $variant }) => ($variant === 'modal' ? 1 : 0.75)}rem;
  background: ${designColors.neutral.lightGray};
  border: 0.0625rem solid ${designColors.neutral.mediumGray};
  border-radius: 0.5rem;
`

const PanelHeading = styled.div<{ $variant: PanelVariant }>`
  display: flex;
  flex-direction: ${({ $variant }) => ($variant === 'modal' ? 'row' : 'column')};
  justify-content: space-between;
  align-items: ${({ $variant }) =>
    $variant === 'modal' ? 'baseline' : 'flex-start'};
  gap: ${({ $variant }) => ($variant === 'modal' ? 1 : 0.125)}rem;
`

const PanelTitle = styled.p<{ $variant: PanelVariant }>`
  ${({ $variant }) =>
    $variant === 'modal' ? Typography.Subtitle1 : Typography.Subtitle2};
  color: ${designColors.neutral.black};
  margin: 0;
`

const PanelLabel = styled.p`
  ${Typography.Body3};
  color: ${designColors.neutral.veryDarkGray};
  margin: 0;

  b {
    color: ${designColors.neutral.black};
  }
`

const PanelHelper = styled.p`
  ${Typography.Body4};
  color: ${designColors.neutral.darkGray};
  margin: 0;
`

type LotCompositionPanelProps = {
  value: ThresholdStep
  // Called once the buyer releases the slider on a new value.
  onCommit: (step: ThresholdStep) => void
  disabled?: boolean
  variant?: PanelVariant
  className?: string
}

/**
 * "Lot composition" panel: the threshold sentence and slider. The sentence
 * follows the thumb while the buyer drags; `onCommit` fires only on release.
 * In the modal the helper line sits beside the title, elsewhere under the
 * slider.
 */
export function LotCompositionPanel({
  value,
  onCommit,
  disabled = false,
  variant = 'rail',
  className,
}: LotCompositionPanelProps) {
  const labelId = useId()
  // Value under the thumb while the buyer is still dragging / pressing keys.
  const [pending, setPending] = useState<ThresholdStep | null>(null)
  const shown = pending ?? value

  const handleRelease = useCallback(() => {
    if (pending === null) return
    setPending(null)
    onCommit(pending)
  }, [onCommit, pending])

  const helper = (
    <PanelHelper>
      <FormattedMessage id="Listing.filters.categoryThreshold.helper" />
    </PanelHelper>
  )

  return (
    <Panel
      className={className}
      $variant={variant}
      data-testid="lot-composition-panel"
    >
      <PanelHeading $variant={variant}>
        <PanelTitle $variant={variant}>
          <FormattedMessage id="Listing.filters.categoryThreshold.title" />
        </PanelTitle>
        {variant === 'modal' && helper}
      </PanelHeading>
      <PanelLabel id={labelId}>
        {disabled ? (
          <FormattedMessage id="Listing.filters.categoryThreshold.disabledLabel" />
        ) : (
          <FormattedMessage
            id="Listing.filters.categoryThreshold.label"
            values={{
              threshold: shown,
              b: (chunks: ReactNode) => <b>{chunks}</b>,
            }}
          />
        )}
      </PanelLabel>
      <ThresholdSlider
        value={shown}
        onChange={setPending}
        onRelease={handleRelease}
        disabled={disabled}
        size={variant === 'mobile' ? 'large' : 'default'}
        labelledBy={labelId}
      />
      {variant !== 'modal' && helper}
    </Panel>
  )
}

const RailPanel = styled(LotCompositionPanel)`
  margin-top: 0.75rem;
`

function ThresholdControlInner() {
  const { state } = useListingSearch()
  const applyThreshold = useApplyThreshold()

  return (
    <RailPanel
      value={toStep(state.filters.categoryThreshold)}
      onCommit={applyThreshold}
    />
  )
}

/**
 * CategoryThresholdControl — live "Lot composition" panel for the category
 * composition threshold. Rendered under the category list in the filter rail,
 * where category changes apply immediately: the new threshold is searched as
 * soon as the buyer releases the slider. The filters modal uses
 * `StagedThresholdPanel` instead.
 *
 * Renders nothing when ENABLE_CATEGORY_COMPOSITION_THRESHOLD is off.
 */
export function CategoryThresholdControl() {
  const enableThreshold = useFlag(
    FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD
  )

  if (!enableThreshold) {
    return null
  }

  return <ThresholdControlInner />
}

/**
 * "Lot composition" panel for the filters modal (list view panel and mobile
 * sheet). Like the category checkboxes there, the threshold is staged in the
 * modal's filter values and applied on "Show results". Disabled until a
 * category is checked, so the threshold never applies to nothing.
 *
 * Renders nothing when ENABLE_CATEGORY_COMPOSITION_THRESHOLD is off.
 */
export function StagedThresholdPanel({
  filterValues,
  onChange,
  className,
}: {
  filterValues: Partial<ListingFiltersState>
  onChange: (step: ThresholdStep) => void
  className?: string
}) {
  const enableThreshold = useFlag(
    FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD
  )

  if (!enableThreshold) {
    return null
  }

  return (
    <LotCompositionPanel
      className={className}
      variant="mobile"
      value={toStep(filterValues.categoryThreshold ?? null)}
      onCommit={onChange}
      disabled={!hasCategorySelection(filterValues)}
    />
  )
}

/**
 * Footer note for the filters modal ("at least 25% of lot"), shown while a
 * category is checked. Renders nothing when the flag is off.
 */
export function StagedThresholdSummaryNote({
  filterValues,
}: {
  filterValues: Partial<ListingFiltersState>
}) {
  const enableThreshold = useFlag(
    FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD
  )

  if (!enableThreshold || !hasCategorySelection(filterValues)) {
    return null
  }

  return (
    <FormattedMessage
      id="Listing.filters.categoryThreshold.summaryNote"
      values={{ threshold: toStep(filterValues.categoryThreshold ?? null) }}
    />
  )
}

/**
 * Staged threshold for the category modals: slider changes are held locally
 * and only applied on "Show results" (`handleCommit`), matching how the modal
 * stages checkbox changes. `handleReset` discards the staged value (Cancel /
 * close).
 */
export const useStagedCategoryThreshold = () => {
  const enabled = useFlag(FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD)
  const { state } = useListingSearch()
  const applyThreshold = useApplyThreshold()
  const [staged, setStaged] = useState<ThresholdStep | null>(null)

  const handleCommit = useCallback(() => {
    if (staged !== null) applyThreshold(staged)
    setStaged(null)
  }, [applyThreshold, staged])

  const handleReset = useCallback(() => setStaged(null), [])

  return {
    enabled,
    staged,
    value: staged ?? toStep(state.filters.categoryThreshold),
    handleChange: setStaged,
    handleCommit,
    handleReset,
  }
}

/**
 * Fires `category_filter_applied` for a category selection that is about to be
 * applied. The threshold is worked out from that selection, not from current
 * state (which still reflects the previous selection): null when the flag is
 * off or nothing will be selected, otherwise the staged value (modal), the
 * explicit one, or the 10% default the search will use.
 */
export const useTrackCategoryFilterApplied = () => {
  const enabled = useFlag(FeatureFlags.ENABLE_CATEGORY_COMPOSITION_THRESHOLD)
  const { state } = useListingSearch()
  const { track } = useAnalytics()
  const currentThreshold = state.filters.categoryThreshold

  return useCallback(
    ({
      categories,
      subcategories,
      stagedThreshold = null,
    }: {
      categories: string[]
      subcategories: string[]
      stagedThreshold?: ThresholdStep | null
    }) => {
      const willFilter =
        enabled && hasCategorySelection({ categories, category: subcategories })

      track('category_filter_applied', {
        categories,
        subcategories,
        threshold: willFilter
          ? (stagedThreshold ?? currentThreshold ?? DEFAULT_THRESHOLD)
          : null,
      })
    },
    [enabled, currentThreshold, track]
  )
}

/**
 * Applied-filters chip for the threshold. Shown only while it actually narrows
 * results: a threshold is set and at least one category is selected (flag-off
 * buyers never have one — `effectiveState` strips it). Not removable on its
 * own: the threshold has a 5% floor and is changed with the slider; "Clear
 * all" resets it with the categories.
 */
export const CategoryThresholdApplied = ({ filtersState }: AppliedProps) => {
  const categoryThreshold = getAppliedThreshold(filtersState)
  if (categoryThreshold === null) return null

  return (
    <AppliedFilterSection
      label="Listing.filters.categoryThreshold.appliedLabel"
      values={[
        {
          key: 'categoryThreshold',
          name: (
            <FormattedMessage
              id="Listing.filters.categoryThreshold.appliedValue"
              values={{ threshold: categoryThreshold }}
            />
          ),
        },
      ]}
      onRemove={() => undefined}
      noClose
    />
  )
}

export default CategoryThresholdControl
